import type { HsHypersceneTypes } from '@lib/core-ts/math-new/types/hyperscene-types.type';
import { getUuid } from '@sibe/utilities/common';
import {
  HsMeasurementTool,
  hsCalibrationScale,
  hsFormatMeasurement,
  hsMeasurementModeLabel,
  type HsMeasurementDeltas,
  type HsMeasurementHit,
  type HsMeasurementMode,
  type HsMeasurementPoint,
  type HsMeasurementStep,
  type HsMeasurementUnit,
  type HsScreenRay,
} from '@sibe/hyperscene/tools/measurement';
import type { HypersceneMeasurementEntry } from './viewer-annotations.store';

/** Saved measurements kept in the browser storage; older ones are dropped. */
const MAX_SAVED_MEASUREMENTS: number = 100;

/** What a canvas click did: nothing to place, a point added, or a measurement completed and saved. */
export type ViewerMeasurementClick =
  | { kind: 'missed' }
  | { kind: 'added'; count: number }
  | { kind: 'saved'; entry: HypersceneMeasurementEntry };

/**
 * The Tools tab's measurements: the SDK tool with the points being placed, whether canvas clicks measure, and the
 * saved measurements. Values come from the tool in meters and are shown in the selected unit, saved ones included.
 * The page passes canvas clicks in and draws the tool's overlay.
 */
export class ViewerMeasurements {
  public readonly tool: HsMeasurementTool = new HsMeasurementTool();
  public enabled: boolean = false;
  public history: HypersceneMeasurementEntry[] = [];
  public selectedId: string | null = null;

  public constructor(private readonly persist: (history: HypersceneMeasurementEntry[]) => void) {}

  /** What to click next for the current mode. */
  public get hint(): string {
    const mode: HsMeasurementMode = this.tool.mode;
    const count: number = this.tool.startsOver ? 0 : this.tool.points.length;
    if (mode === 'point') {
      return 'Click a point to read its coordinates.';
    }
    if (mode === 'distance') {
      return count === 0 ? 'Click the start point.' : 'Click the end point.';
    }
    if (mode === 'angle') {
      return ['Click a point on the first arm.', 'Click the vertex.', 'Click a point on the second arm.'][count];
    }
    return count < (mode === 'area' ? 3 : 2)
      ? 'Click the next corner.'
      : 'Click more points, or Save to keep the measurement.';
  }

  /** Placed points and where they lie: `3 (on surfaces)`. */
  public get pointsLabel(): string {
    const count: number = this.tool.points.length;
    if (count === 0) {
      return 'none';
    }
    const onLevel: number = this.tool.sources.filter((source: string) => source === 'plane').length;
    return onLevel === 0 ? `${count} (on surfaces)` : `${count} (${onLevel} on the first point's level)`;
  }

  public get lastPointLabel(): string {
    const points: readonly HsMeasurementPoint[] = this.tool.points;
    const point: HsMeasurementPoint | undefined = points[points.length - 1];
    return point === undefined ? 'n/a' : point.map((value: number) => value.toFixed(3)).join(', ');
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.tool.clear();
  }

  public setMode(value: string): void {
    this.tool.mode =
      value === 'angle' || value === 'polyline' || value === 'area' || value === 'point' ? value : 'distance';
    this.tool.clear();
  }

  public setSnapMode(value: string): void {
    this.tool.snapMode = value === 'selected-node' || value === 'plane' ? value : 'auto';
  }

  public setUnit(value: string): void {
    this.tool.unit = value === 'cm' || value === 'mm' ? value : 'm';
  }

  /**
   * Places a canvas click: `hit` is the drawn surface under the cursor, `ray` the camera ray through it. A
   * distance or an angle is saved as soon as it has its points.
   */
  public click(
    hit: HsMeasurementHit | null,
    ray: HsScreenRay | null,
    selectedNodeId: string | null
  ): ViewerMeasurementClick {
    const step: HsMeasurementStep | null = this.tool.place(hit, ray, selectedNodeId);
    if (step === null) {
      return { kind: 'missed' };
    }
    const entry: HypersceneMeasurementEntry | null = step === 'completed' ? this.save() : null;
    return entry === null ? { kind: 'added', count: this.tool.points.length } : { kind: 'saved', entry };
  }

  /** Saves the current measurement, or returns null while it has too few points. */
  public save(): HypersceneMeasurementEntry | null {
    const value: number | null = this.tool.value;
    if (value === null) {
      return null;
    }
    const points: HsHypersceneTypes.Array3[] = this.tool.points.map((point: HsMeasurementPoint) => [
      point[0],
      point[1],
      point[2],
    ]);
    const entry: HypersceneMeasurementEntry = {
      id: getUuid(),
      pointA: points[0],
      pointB: points[1] ?? points[0],
      points,
      distance: value,
      mode: this.tool.mode,
      source: this.tool.source,
      unit: this.tool.unit,
      scale: this.tool.scale,
      createdAt: new Date().toISOString(),
    };
    this.history = [entry, ...this.history].slice(0, MAX_SAVED_MEASUREMENTS);
    this.selectedId = entry.id;
    this.persist(this.history);
    return entry;
  }

  /**
   * Calibrates the scene's model units from the current distance or path, whose real length is `realMeters`;
   * returns the scale, or null without a measured length.
   */
  public calibrate(realMeters: number): number | null {
    const raw: number | null = this.tool.rawValue;
    if ((this.tool.mode !== 'distance' && this.tool.mode !== 'polyline') || raw === null || raw <= 0) {
      return null;
    }
    const scale: number = hsCalibrationScale(raw, realMeters);
    this.tool.scale = scale;
    return scale;
  }

  /** The distance split along the axes, in the selected unit: `Δx 3.000 · Δy 2.100 · Δz 4.000 · horizontal …`. */
  public get deltasLabel(): string | null {
    const deltas: HsMeasurementDeltas | null = this.tool.deltas;
    if (deltas === null) {
      return null;
    }
    const unit: HsMeasurementUnit = this.tool.unit;
    const format = (value: number): string => hsFormatMeasurement('distance', value, unit);
    return (
      `Δx ${format(deltas.dx)} · Δy ${format(deltas.dy)} · Δz ${format(deltas.dz)} · ` +
      `horizontal ${format(deltas.horizontal)} · vertical ${format(deltas.vertical)}`
    );
  }

  /** Takes the saved measurements read from the browser storage. */
  public restore(history: HypersceneMeasurementEntry[]): void {
    this.history = history;
    this.selectedId = history[0]?.id ?? null;
  }

  /** Selects a saved measurement and shows its points, or clears the points for null. */
  public select(id: string | null): HypersceneMeasurementEntry | null {
    this.selectedId = id;
    const entry: HypersceneMeasurementEntry | undefined = this.history.find(
      (item: HypersceneMeasurementEntry) => item.id === id
    );
    if (entry === undefined) {
      this.tool.clear();
      return null;
    }
    this.tool.mode = entry.mode ?? 'distance';
    this.tool.setPoints(
      entry.points === undefined || entry.points.length === 0 ? [entry.pointA, entry.pointB] : entry.points,
      entry.source
    );
    return entry;
  }

  public deleteSelected(): void {
    this.history = this.history.filter((item: HypersceneMeasurementEntry) => item.id !== this.selectedId);
    this.selectedId = this.history[0]?.id ?? null;
    this.persist(this.history);
  }

  public clearHistory(): void {
    this.history = [];
    this.selectedId = null;
    this.persist(this.history);
  }

  /** A saved value in the selected unit: values are stored in meters whatever unit was shown when saving. */
  public formatValue(entry: HypersceneMeasurementEntry): string {
    return hsFormatMeasurement(entry.mode ?? 'distance', entry.distance, this.tool.unit);
  }

  /** A saved measurement for the list: `Distance: 2.100 m (on surfaces)`. */
  public formatEntry(entry: HypersceneMeasurementEntry): string {
    const placement: string = entry.source === 'snap' ? 'on surfaces' : 'partly on a level';
    return `${hsMeasurementModeLabel(entry.mode ?? 'distance')}: ${this.formatValue(entry)} (${placement})`;
  }
}
